import { useState, useEffect, useRef } from "react";
import { Box, IconButton, Stack, Typography, Tooltip } from "@mui/material";
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  ZoomOutMap as ZoomResetIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";

const IMAGE_TYPES = ["jpg", "jpeg", "png", "gif", "bmp", "tiff", "tif", "svg", "webp"];

export default function FilePreviewViewer({ url, filename, type }) {
  const [scale, setScale] = useState(1);
  const [blobUrl, setBlobUrl] = useState(null);
  const containerRef = useRef(null);

  const isImage = IMAGE_TYPES.includes(type);
  const isPdf = type === "pdf";

  useEffect(() => {
    if (!isPdf) return;
    let objectUrl;
    fetch(url)
      .then((res) => res.blob())
      .then((blob) => {
        objectUrl = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
        setBlobUrl(objectUrl);
      })
      .catch(() => setBlobUrl("error"));
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, isPdf]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !isImage) return;
    const onWheel = (e) => {
      e.preventDefault();
      setScale((prev) => Math.min(5, Math.max(0.2, prev - e.deltaY * 0.001)));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [isImage]);

  const handleDownload = () => {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
  };

  return (
    <Stack sx={{ width: "80vw", maxWidth: 1200, height: "85vh" }}>
      {/* Toolbar */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: 2, py: 1, borderBottom: "1px solid #e0e0e0", flexShrink: 0 }}
      >
        <Typography
          variant="subtitle1"
          sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          {filename}
        </Typography>
        {isImage && (
          <>
            <Tooltip title="Zoom out">
              <span>
                <IconButton
                  onClick={() => setScale((s) => Math.max(0.2, +(s - 0.2).toFixed(1)))}
                  disabled={scale <= 0.2}
                >
                  <ZoomOutIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Typography variant="body2" sx={{ minWidth: 44, textAlign: "center" }}>
              {Math.round(scale * 100)}%
            </Typography>
            <Tooltip title="Zoom in">
              <span>
                <IconButton
                  onClick={() => setScale((s) => Math.min(5, +(s + 0.2).toFixed(1)))}
                  disabled={scale >= 5}
                >
                  <ZoomInIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Reset zoom">
              <IconButton onClick={() => setScale(1)}>
                <ZoomResetIcon />
              </IconButton>
            </Tooltip>
          </>
        )}
        <Tooltip title="Download">
          <IconButton onClick={handleDownload}>
            <DownloadIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* PDF */}
      {isPdf && !blobUrl && (
        <Stack alignItems="center" justifyContent="center" sx={{ flex: 1, bgcolor: "#f5f5f5" }}>
          <Typography color="text.secondary">Loading...</Typography>
        </Stack>
      )}
      {isPdf && blobUrl === "error" && (
        <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ flex: 1, bgcolor: "#f5f5f5" }}>
          <Typography color="text.secondary">Cannot preview this file.</Typography>
          <IconButton onClick={handleDownload} size="large" color="primary">
            <DownloadIcon fontSize="large" />
          </IconButton>
          <Typography variant="body2" color="text.secondary">Click to download</Typography>
        </Stack>
      )}
      {isPdf && blobUrl && blobUrl !== "error" && (
        <object
          data={`${blobUrl}#toolbar=0`}
          type="application/pdf"
          style={{ flex: 1, width: "100%", minHeight: 0, border: "none", display: "block" }}
        />
      )}

      {/* Image */}
      {isImage && (
        <Box
          ref={containerRef}
          sx={{ flex: 1, overflow: "auto", display: "flex", justifyContent: "center", alignItems: "flex-start", bgcolor: "#f5f5f5" }}
        >
          <Box
            component="img"
            src={url}
            alt={filename}
            sx={{
              transform: `scale(${scale})`,
              transformOrigin: "top center",
              transition: "transform 0.1s",
              maxWidth: "100%",
              display: "block",
              mt: scale > 1 ? `${(scale - 1) * 50}%` : 0,
            }}
          />
        </Box>
      )}

      {/* Unsupported types */}
      {!isImage && !isPdf && (
        <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ flex: 1, bgcolor: "#f5f5f5" }}>
          <Typography variant="h6" color="text.secondary">
            Preview not available for .{type} files
          </Typography>
          <IconButton onClick={handleDownload} size="large" color="primary">
            <DownloadIcon fontSize="large" />
          </IconButton>
          <Typography variant="body2" color="text.secondary">Click to download</Typography>
        </Stack>
      )}
    </Stack>
  );
}
